<template>
  <div class="data-variance">
    <div class="controls">
      <div class="control-item">
        <label for="dataset-select">Dataset:</label>
        <select
          id="dataset-select"
          v-model="selectedDatasetId"
          @change="onDatasetChange"
        >
          <option
            v-for="ds in datasets"
            :key="ds.DatasetId"
            :value="ds.DatasetId"
          >
            {{ ds.DatasetId }} — {{ ds.CreatedOn }}
          </option>
        </select>
      </div>
      <div class="control-item">
        <label>Units:</label>
        <SearchableDropdownCheckboxes
          :options="unitOptions"
          v-model="selectedUnits"
          buttonLabel="Select Units…"
        />
      </div>
      <div class="control-item">
        <button @click="fetchVariance" :disabled="loading || !canFetch">
          {{ loading ? "Loading…" : "Fetch Variance" }}
        </button>
      </div>
      <div v-if="jobStatus">
        <p>Status: {{ jobStatus.status }}</p>
        <p v-if="jobStatus.eta">ETA: {{ formattedEta }}</p>
      </div>
    </div>

    <div v-if="varianceLoaded" class="results">
      <!-- BOX PLOT SECTION -->
      <div class="chart-section">
        <h2>Distribution Differences by Band (boxplot)</h2>
        <div class="chart-container">
          <VChart :option="boxChartOptions" autoresize />
        </div>
      </div>

      <!-- BAR PLOT SECTION WITH STATISTIC DROPDOWN -->
      <div class="chart-section">
        <div class="chart-controls">
          <label for="stat-select">Statistic:</label>
          <select id="stat-select" v-model="selectedBarMetric">
            <option v-for="m in barMetrics" :key="m" :value="m">{{ m }}</option>
          </select>
        </div>
        <h2>{{ selectedBarMetric }} Differences by Band</h2>
        <div class="chart-container">
          <VChart :option="barChartOptions" autoresize />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { api_config } from "@/env.ts";
import axios from "axios";
import { getToken } from "@/keycloak-wrapper.ts";
import VChart from "vue-echarts";
import SearchableDropdownCheckboxes from "@/components/widgets/SearchableDropdownCheckboxes.vue";

export default {
  name: "DataVariance",
  components: {
    VChart,
    SearchableDropdownCheckboxes,
  },
  data() {
    return {
      token: null,
      jobStatus: null,
      eta: null,
      now: null,
      timerId: null,

      datasets: [],
      unitOptions: [],
      selectedDatasetId: null,
      selectedUnits: [],

      loading: false,
      varianceLoaded: false,
      varianceData: {
        precomputed_stats: {},
        live_unit_stats: {},
      },

      barMetrics: [
        "Min",
        "Max",
        "Mean",
        "Median",
        "Variance",
        "Std",
        "P5",
        "P95",
        "IQR",
        "Skewness",
        "Kurtosis",
      ],
      selectedBarMetric: "Max",

      // chart option holders
      boxChartOptions: {},
      barChartOptions: {},
    };
  },
  computed: {
    canFetch() {
      return this.selectedDatasetId && this.selectedUnits.length;
    },
    varianceJson() {
      return JSON.stringify(this.varianceData, null, 2);
    },
    formattedEta() {
      if (!this.jobStatus || !this.eta) return;
      let seconds = Math.max(0, Math.round((this.eta - this.now) / 1000)); // remaining seconds
      const hours = Math.floor(seconds / 3600);
      seconds %= 3600;
      const minutes = Math.floor(seconds / 60);
      seconds %= 60;

      const parts = [];
      if (hours) parts.push(`${hours}h`);
      if (minutes) parts.push(`${minutes}m`);
      parts.push(`${seconds}s`);

      return parts.join(" ");
    },
  },
  watch: {
    // whenever user picks a new stat, rebuild the bar chart
    selectedBarMetric() {
      if (this.varianceLoaded) {
        this.updateChartOptions();
      }
    },
  },
  async mounted() {
    this.token = await getToken();
    this.fetchDatasets();
    this.fetchUnitOptions();
    this.timerId = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  unmounted() {
    clearInterval(this.timerId);
  },
  methods: {
    authHeaders() {
      return {
        "Content-Type": "application/json",
        Authorization: `Bearer ${this.token}`,
      };
    },
    async fetchDatasets() {
      try {
        const { data } = await axios.get(`${api_config.ml_backend}/datasets`, {
          headers: this.authHeaders(),
        });
        this.datasets = data.datasets;
        this.selectedDatasetId =
          this.datasets.length > 0 ? this.datasets[0].DatasetId : null;
      } catch (e) {
        console.error("Error fetching datasets", e);
      }
    },
    async fetchUnitOptions() {
      try {
        const { data } = await axios.get(
          `${api_config.ml_backend}/SentinexUnits`,
          {
            headers: this.authHeaders(),
          }
        );
        this.unitOptions = data.unit_list.map((u) => ({ value: u, label: u }));
      } catch (e) {
        console.error("Error fetching units", e);
      }
    },
    async onDatasetChange() {
      this.varianceLoaded = false;
    },
    async fetchVariance() {
      if (!this.canFetch) return;
      this.loading = true;
      this.varianceLoaded = false;
      try {
        const startResponse = await axios.post(
          `${api_config.ml_backend}/datasets/${this.selectedDatasetId}/dataVarianceTo`,
          { unit_ids: this.selectedUnits },
          { headers: this.authHeaders() }
        );
        const jobId = startResponse.data.job_id;

        const pollStatus = async () => {
          let attempt = 0;
          while (attempt < 100) {
            // limit attempts to avoid infinite loop
            const { data: status } = await axios.get(
              `${api_config.ml_backend}/datasets/dataVarianceTo/status/${jobId}`,
              { headers: this.authHeaders() }
            );
            this.jobStatus = status;
            this.eta = Date.now() + status.eta * 1000; // seconds to ms
            if (status.status === "completed") {
              return true;
            } else if (status.status === "failed") {
              throw new Error(`Background task failed: ${status.error}`);
            }
            attempt++;
            await new Promise((r) => setTimeout(r, 30000)); // wait 30s
          }
          throw new Error("Job timeout exceeded");
        };

        const success = await pollStatus();

        if (success) {
          const { data: result } = await axios.get(
            `${api_config.ml_backend}/datasets/dataVarianceTo/result/${jobId}`,
            { headers: this.authHeaders() }
          );
          this.varianceData = result;
          this.varianceLoaded = true;
          this.updateChartOptions();
        }
      } catch (e) {
        console.error("Error fetching variance data", e);
        alert(`Error: ${e.response?.data?.detail || e.message}`);
      } finally {
        this.loading = false;
      }
    },
    updateChartOptions() {
      const pre = this.varianceData.precomputed_stats || {};
      const live = this.varianceData.live_unit_stats || {};
      const bands = Object.keys(pre).sort((a, b) => {
        const toHz = (s) =>
          parseFloat(s) * (s.toLowerCase().endsWith("khz") ? 1000 : 1);
        return toHz(a) - toHz(b);
      });

      // 1) BOX PLOT data = [ Min, Q1, Median, Q3, P95 ] difference
      const boxData = bands.map((b) => {
        const p = pre[b] || {};
        const l = live[b] || {};
        return [
          Number((l.Min - p.Min).toFixed(2)),
          Number((l.Q1 - p.Q1).toFixed(2)),
          Number((l.Median - p.Median).toFixed(2)),
          Number((l.Q3 - p.Q3).toFixed(2)),
          Number((l.P95 - p.P95).toFixed(2)),
        ];
      });
      this.boxChartOptions = {
        tooltip: {},
        xAxis: {
          type: "category",
          data: bands,
          axisLabel: { rotate: 45, interval: 0 },
        },
        yAxis: { type: "value", name: "Live – Precomputed" },
        grid: { top: "40px", bottom: "60px", left: "60px", right: "20px" },
        dataZoom: [
          { type: "slider", xAxisIndex: 0, start: 0, end: 100 },
          { type: "inside", xAxisIndex: 0, start: 0, end: 100 },
        ],
        series: [
          {
            name: "Distribution diff",
            type: "boxplot",
            data: boxData,
          },
        ],
      };

      // 2) BAR PLOT for the selected statistic
      const barData = bands.map((b) => {
        const p = pre[b]?.[this.selectedBarMetric] || 0;
        const l = live[b]?.[this.selectedBarMetric] || 0;
        return Number((l - p).toFixed(2));
      });
      this.barChartOptions = {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        xAxis: {
          type: "category",
          data: bands,
          axisLabel: { rotate: 45, interval: 0 },
        },
        yAxis: {
          type: "value",
          name: `${this.selectedBarMetric} (Live – Precomputed)`,
        },
        grid: { top: "40px", bottom: "60px", left: "60px", right: "20px" },
        dataZoom: [
          { type: "slider", xAxisIndex: 0, start: 0, end: 100 },
          { type: "inside", xAxisIndex: 0, start: 0, end: 100 },
        ],
        series: [
          {
            name: this.selectedBarMetric,
            type: "bar",
            data: barData,
          },
        ],
      };
    },
  },
};
</script>

<style scoped>
.data-variance {
  display: grid;
  grid-template-rows: auto 1fr;
  height: 100%;
  width: 100%;
  gap: 1rem;
}
.controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}
.control-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}
.controls button {
  align-self: flex-end;
  margin-top: 1.5rem;
}
.results {
  display: grid;
  grid-template-rows: 1fr 1fr;
  gap: 1rem;
}
.chart-section {
  display: flex;
  flex-direction: column;
  background: #f9f9f9;
  padding: 0.5rem;
  border: 1px solid var(--colour-border);
  border-radius: var(--radius-sm);
}
.chart-controls {
  display: flex;
  gap: 1rem;
  align-items: center;
}
.chart-container {
  flex: 1; /* fill the remaining space in each .chart-section */
  min-height: 0; /* again, so it can actually fit */
}
.details-section {
  background: #fdfdfd;
  padding: 0.5rem;
  border: 1px solid var(--colour-border);
  border-radius: var(--radius-sm);
  overflow: auto;
}
.stats-textarea {
  width: 100%;
  font-family: monospace;
  font-size: 0.9rem;
}
</style>
